import React, { useEffect, useState, useRef, useCallback } from "react";
import styles from "./AssetBalances.module.sass";
import { useSelector, useDispatch } from "react-redux";
import { fetchAssets, fetchReferenceCurrency } from "../../../store/general/assets/actions";
import { Spinner, Form } from "react-bootstrap";

const AssetBalances = () => {
  const dispatch = useDispatch();
  const { exchangeWallets, loading } = useSelector((state) => state?.walletReducer);
  const { assets } = useSelector((state) => state?.assets);
  const { referenceCurrency } = useSelector((state) => state?.assets);
  
  // Infinite scroll state
  const [displayCount, setDisplayCount] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const loaderRef = useRef(null);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!assets) {
      dispatch(fetchAssets());
    }
    
    if (!referenceCurrency?.data) {
      dispatch(fetchReferenceCurrency());
    }
  }, [dispatch, assets, referenceCurrency]);
  
  // Filter wallets based on search term
  const filteredWallets = exchangeWallets?.filter(wallet => {
    return wallet.asset?.toLowerCase().includes(searchTerm.toLowerCase());
  }) || [];
  
  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setDisplayCount(10); // Reset display count when searching
  };
  
  // Get current items to display
  const currentItems = filteredWallets.slice(0, displayCount);
  
  // Implement infinite scroll using Intersection Observer
  const loadMoreItems = useCallback(() => {
    if (!isLoading && filteredWallets.length > displayCount) {
      setIsLoading(true);
      // Simulate loading delay
      setTimeout(() => {
        setDisplayCount(prevCount => prevCount + 10);
        setIsLoading(false);
      }, 500);
    }
  }, [isLoading, filteredWallets.length, displayCount]);
  
  // Set up intersection observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          loadMoreItems();
        }
      },
      { threshold: 0.1 }
    );
    
    const currentLoaderRef = loaderRef.current;
    if (currentLoaderRef) {
      observer.observe(currentLoaderRef);
    }
    
    return () => {
      if (currentLoaderRef) {
        observer.unobserve(currentLoaderRef);
      }
    };
  }, [loadMoreItems, loaderRef]);
  
  return (
    <div className={styles.wrap}>
      {/* Search input */}
      <div className={styles.searchContainer}>
        <Form.Group className="mb-3">
          <Form.Control
            type="text"
            placeholder="Search by asset name"
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </Form.Group>
      </div>
      
      <div className={styles.table}>
        <div className={styles.row}>
          <div className={styles.col}>Asset</div>
          <div className={styles.col}>Balance</div>
        </div>
        {loading ? (
          <div className="d-flex align-items-center justify-content-center mt-5 mb-5">
            <Spinner />
          </div>
        ) : filteredWallets.length > 0 ? (
          currentItems.map((x, index) => (
            <div className={styles.row} key={index}>
              <div className={styles.col}>
                <div className={styles.asset}>
                  <div className={styles.icon}>
                    <img
                      src={`https://huobicfg.s3.amazonaws.com/currency_icon/${x.asset}.png`}
                      alt={x.asset}
                    />
                  </div>
                  <div className={styles.details}>
                    <div className={styles.info}>{x.asset?.toUpperCase()}</div>
                  </div>
                </div>
              </div>
              <div className={styles.col}>
                <div className={styles.text}>{x.balance}</div>
              </div>
            </div>
          ))
        ) : (
          <div className="d-flex align-items-center justify-content-center mt-5 mb-5">
            No assets found matching your search.
          </div>
        )}
      </div>
      
      {/* Infinite scroll loader */}
      {!loading && filteredWallets.length > displayCount && (
        <div ref={loaderRef} className={styles.loaderContainer}>
          {isLoading ? (
            <>
              <Spinner animation="border" size="sm" />
              <span>Loading more...</span>
            </>
          ) : (
            <span>Scroll for more</span>
          )}
        </div>
      )}
    </div>
  );
};

export default AssetBalances;
